import { useEffect, useMemo, useState } from 'react';
import { useRow, useRows } from '@/hooks/useLive';
import { save, softDelete, upsert } from '@/lib/crud';
import { hours as fmtHours, num, isoDate } from '@/lib/format';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import { StatusChip } from '@/components/ui/StatusChip';
import {
  NumberInput, SelectInput, TextArea, TextInput,
} from '@/components/ui/Field';
import type { Machine } from '@/types/models';

interface Liner extends Record<string, unknown> {
  id: string;
  machine_id: string;
  component_id: string | null;
  part_id: string | null;
  part_no: string | null;
  description: string;
  installed_on: string;
  installed_hour_meter: number;
  removed_on: string | null;
  removed_hour_meter: number | null;
  quantity: number;
  supplier_id: string | null;
  unit_cost: number | null;
  expected_life_hours: number | null;
  reason_for_replacement: string | null;
  condition_on_removal: string | null;
  is_current: boolean;
  comments: string | null;
}

interface Component extends Record<string, unknown> {
  id: string;
  machine_id: string;
  name: string;
  position: string | null;
  expected_life_hours: number | null;
  is_active: boolean;
}

const CONDITIONS = [
  { value: 'worn_out', label: 'Worn out — normal life' },
  { value: 'premature', label: 'Failed early' },
  { value: 'damaged', label: 'Damaged / broken' },
  { value: 'preventive', label: 'Changed preventively' },
];

/**
 * Liner life is the difference between two hour-meter readings, nothing
 * more — which is why both readings are required and the drawer refuses to
 * accept a removal that reads lower than the installation.
 */
export function LinerDrawer({
  record, onClose, locked,
}: { record: Partial<Liner>; onClose: () => void; locked?: string | null }) {
  const isNew = !record.id;
  const stored = useRow<Liner>('liner_installations', record.id);
  const machines = useRows<Machine>('machines', 'fleet_no');
  const components = useRows<Component>('machine_components', 'name');
  const suppliers = useRows<Record<string, unknown>>('suppliers', 'name');
  const parts = useRows<Record<string, unknown>>('parts', 'part_no');

  const [v, setV] = useState<Partial<Liner>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setV({
      installed_on: isoDate(),
      quantity: 1,
      is_current: true,
      ...record,
      ...(stored ?? {}),
    });
  }, [record, stored]);

  const set = (k: string, val: unknown) => setV((d) => ({ ...d, [k]: val }));

  const machine = useMemo(
    () => (machines ?? []).find((m) => m.id === v.machine_id),
    [machines, v.machine_id],
  );
  const mine = useMemo(
    () => (components ?? []).filter((c) => c.machine_id === v.machine_id && c.is_active),
    [components, v.machine_id],
  );

  const installed = v.installed_hour_meter == null ? null : Number(v.installed_hour_meter);
  const removed = v.removed_hour_meter == null ? null : Number(v.removed_hour_meter);
  const meter = Number(machine?.current_hour_meter ?? 0);

  const life = installed != null && removed != null ? removed - installed : null;
  const inService = installed != null && removed == null && machine ? meter - installed : null;
  const expected = v.expected_life_hours == null ? null : Number(v.expected_life_hours);

  function pickComponent(id: string | null) {
    set('component_id', id);
    const c = mine.find((x) => x.id === id);
    // The component carries the expected life. Copying it onto the
    // installation is what lets the expectation change later without
    // rewriting the history of liners already fitted.
    if (c?.expected_life_hours != null && v.expected_life_hours == null) {
      set('expected_life_hours', c.expected_life_hours);
    }
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!v.machine_id) e.machine_id = 'Choose the machine';
    if (!v.description) e.description = 'Describe the liner or wear part';
    if (!v.installed_on) e.installed_on = 'Installation date is required';
    if (installed == null) e.installed_hour_meter = 'The hour meter at installation is the start of the life';
    if (removed != null && installed != null && removed < installed) {
      e.removed_hour_meter = 'The removal reading is below the installation reading';
    }
    if (v.removed_on && v.installed_on && String(v.removed_on) < String(v.installed_on)) {
      e.removed_on = 'Removed before it was installed';
    }
    if (removed != null && !v.removed_on) e.removed_on = 'Give the removal date as well';
    if (v.removed_on && removed == null) {
      e.removed_hour_meter = 'Give the hour meter at removal — it is what measures the life';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setBusy(true);
    const payload: Record<string, unknown> = { ...v };
    if (record.id) payload.id = record.id;
    const ok = await save(
      () => upsert('liner_installations', payload as Record<string, unknown> & { id?: string }),
      removed != null ? 'Liner removed — life recorded' : 'Liner installation saved',
    );
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <Drawer
      open
      locked={locked}
      width={560}
      title={isNew ? 'Install a liner' : 'Liner installation'}
      subtitle={machine ? `${machine.fleet_no} · meter ${num(meter)} h` : undefined}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <ConfirmRemove
              disabled={busy}
              label="Remove"
              question="Remove this liner record?"
              onConfirm={async () => {
                const ok = await save(() => softDelete('liner_installations', record.id as string), 'Removed');
                // Close only if it went through. A refusal leaves the record
                // open with the reason on screen, rather than closing over it.
                if (ok !== null) onClose();
                return ok !== null;
              }}
            />
          )}
          <span className="spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="form-grid">
        <SelectInput id="li-machine" label="Machine" required error={errors.machine_id}
          value={(v.machine_id as string) ?? null}
          options={(machines ?? []).filter((m) => m.is_active)
            .map((m) => ({ value: m.id, label: m.fleet_no }))}
          onChange={(x) => set('machine_id', x)} />
        <SelectInput id="li-comp" label="Component" value={(v.component_id as string) ?? null}
          options={mine.map((c) => ({
            value: c.id,
            label: c.position ? `${c.name} — ${c.position}` : c.name,
          }))}
          onChange={pickComponent}
          help={v.machine_id && mine.length === 0
            ? 'This machine has no components registered yet.' : undefined} />

        <div className="field--full">
          <TextInput id="li-desc" label="Liner / wear part" required error={errors.description}
            value={String(v.description ?? '')} onChange={(x) => set('description', x)} />
        </div>

        <SelectInput id="li-part" label="Stock part" value={(v.part_id as string) ?? null}
          options={(parts ?? []).map((p) => ({
            value: String(p.id), label: `${p.part_no} — ${p.description}`,
          }))}
          onChange={(x) => {
            set('part_id', x);
            const p = (parts ?? []).find((r) => String(r.id) === x);
            if (p) set('part_no', String(p.part_no));
          }}
          help="Optional — links the change to the stock ledger." />
        <TextInput id="li-partno" label="Part number" value={String(v.part_no ?? '')}
          onChange={(x) => set('part_no', x || null)} />

        <TextInput id="li-in-d" label="Installed on" type="date" required error={errors.installed_on}
          value={String(v.installed_on ?? '')} onChange={(x) => set('installed_on', x)} />
        <NumberInput id="li-in-h" label="Hour meter at installation" suffix="h" min={0} step={1}
          required error={errors.installed_hour_meter}
          value={(v.installed_hour_meter as number) ?? null}
          onChange={(x) => set('installed_hour_meter', x)} />

        <TextInput id="li-out-d" label="Removed on" type="date" error={errors.removed_on}
          value={String(v.removed_on ?? '')} onChange={(x) => set('removed_on', x || null)} />
        <NumberInput id="li-out-h" label="Hour meter at removal" suffix="h" min={0} step={1}
          error={errors.removed_hour_meter}
          value={(v.removed_hour_meter as number) ?? null}
          onChange={(x) => set('removed_hour_meter', x)} />

        <div className="field--full">
          <div className="engine-hours">
            <span className="engine-hours__value">
              {life != null ? fmtHours(life) : inService != null ? fmtHours(inService) : '—'}
            </span>
            <span className="engine-hours__label">
              {life != null ? 'hours life achieved' : 'hours in service so far'}
            </span>
            {expected != null && (life ?? inService) != null && (
              <StatusChip tone={(life ?? inService ?? 0) >= expected ? 'green' : 'amber'}>
                {num(Math.round(((life ?? inService ?? 0) / expected) * 100))}% of expected
              </StatusChip>
            )}
          </div>
        </div>

        <NumberInput id="li-exp" label="Expected life" suffix="h" min={0} step={100}
          value={(v.expected_life_hours as number) ?? null}
          onChange={(x) => set('expected_life_hours', x)} />
        <NumberInput id="li-qty" label="Quantity" min={0.01} step={1}
          value={(v.quantity as number) ?? 1} onChange={(x) => set('quantity', x ?? 1)} />

        <SelectInput id="li-sup" label="Supplier" value={(v.supplier_id as string) ?? null}
          options={(suppliers ?? []).map((s) => ({ value: String(s.id), label: String(s.name) }))}
          onChange={(x) => set('supplier_id', x)} />
        <NumberInput id="li-cost" label="Unit cost" min={0} step={100}
          value={(v.unit_cost as number) ?? null} onChange={(x) => set('unit_cost', x)}
          help={v.unit_cost != null && v.quantity != null
            ? `Total ${num(Number(v.unit_cost) * Number(v.quantity), 2)}` : undefined} />

        <SelectInput id="li-cond" label="Condition on removal"
          value={(v.condition_on_removal as string) ?? null} options={CONDITIONS}
          onChange={(x) => set('condition_on_removal', x)} />
        <TextInput id="li-reason" label="Reason for replacement"
          value={String(v.reason_for_replacement ?? '')}
          onChange={(x) => set('reason_for_replacement', x || null)} />

        <div className="field--full">
          <TextArea id="li-comments" label="Comments" value={String(v.comments ?? '')}
            onChange={(x) => set('comments', x || null)} />
        </div>
      </div>

      {removed != null && (
        <p className="small muted" style={{ marginTop: 'var(--sp-3)', marginBottom: 0 }}>
          Recording a removal closes this installation, so the machine and component
          are free for the next liner to be fitted.
        </p>
      )}
    </Drawer>
  );
}
